const formatterCache: Record<string, Record<string, Intl.NumberFormat>> = {};
/**
 * Formats a number as a shortened currency string with K, M, B, or T suffix.
 * 
 * @param input - The number to format
 * @param locale - The locale to use for formatting (default: "en-US")
 * @param currency - The currency code to use (default: "USD")
 * @returns A formatted string (e.g., "$1M" for 1,000,000)
 */
export function shortCurrency(
  input?: number,
  locale: string = "en-US",
  currency: string = "USD",
): string {
  if (input === undefined || input === null) {
    input = 0;
  }

  const absValue = Math.abs(input);
  const sign = input < 0 ? "-" : "";
  
  // Get currency symbol
  const formatter = new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency,
    maximumFractionDigits: 0,
  });
  
  // Extract just the currency symbol
  const parts = formatter.formatToParts(0);
  const currencySymbol = parts.find(part => part.type === "currency")?.value || "$";
  
  if (absValue >= 1_000_000_000_000) {
    return `${sign}${currencySymbol}${(absValue / 1_000_000_000_000).toFixed(1)}T`;
  } else if (absValue >= 1_000_000_000) {
    return `${sign}${currencySymbol}${(absValue / 1_000_000_000).toFixed(1)}B`;
  } else if (absValue >= 1_000_000) {
    return `${sign}${currencySymbol}${(absValue / 1_000_000).toFixed(1)}M`;
  } else if (absValue >= 1_000) {
    return `${sign}${currencySymbol}${(absValue / 1_000).toFixed(1)}K`;
  } else {
    return formatter.format(input);
  }
}

export function rate(
  input?: number,
) {
  return Math.round((input ?? 0) * 100);
}

export function currency(
  input?: number,
  fractionDigits: number = 0,
  locale: string = "en-US",
  currency: string = "USD",
) {
  let localeCache = formatterCache[locale];
  if (!localeCache) {
    localeCache = formatterCache[locale] = {};
  }

  let formatter = localeCache[currency];

  if (!formatter) {
    formatter = localeCache[currency] = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency,
      maximumFractionDigits: fractionDigits,
    });
  }

  return formatter.format(input ?? 0);
}
